// @flow
import { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import includes from 'lodash/includes';

import type { LogInActionCreator } from '../../typings';
import RoundedCornerButton from '../RoundedCornerButton';
import opacityRevealKeyframes from '../../../keyframes/opacityRevealKeyframes';
import opacityHideKeyframes from '../../../keyframes/opacityHideKeyframes';
import pinDelete from '../../../assets/svg/icons/pin-delete.svg';
import { ENTER_KEY_CODE, BACKSPACE_KEY_CODE } from '../../../constants';

import {
  PIN_PAD_WIDTH,
  PIN_PAD_MINIMAL_HEIGHT,
  DEFAULT_PIN_PAD_MARGIN,
  COLLIDE_WIDTH,
  COLLIDE_HEIGHT
} from './constants';
import PinDisplay from './PinDisplay';
import PinPadButton from './PinPadButton';

const Container = styled.div`
  width: ${props => props.scale * PIN_PAD_WIDTH}px;
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  margin-top: 8px;

  &.visible {
    opacity: 0;
    animation: ${opacityRevealKeyframes} 0.3s ease-in-out;
    animation-delay: ${props => (props.isFirstRender ? '0.6s' : 0)};
    animation-fill-mode: forwards;
  }

  &.hidden {
    opacity: 1;
    animation: ${opacityHideKeyframes} 0.3s ease-in-out;
    animation-fill-mode: forwards;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.marginTop}px;
`;

const EnterButton = styled.div`
  margin-top: ${props => props.marginTop}px;
`;

type Props = {
  pinPadInitialMount: boolean,
  shouldShow: boolean,
  deviceHeight: number,
  deviceWidth: number,
  logIn: LogInActionCreator,
  onVisibilityChange: (isVisible: boolean) => void
};

type State = {
  pin: string
};

export default class PinPad extends PureComponent<Props, State> {
  componentDidMount() {
    document.addEventListener('keypress', this.keypressEventHandler);
    document.addEventListener('keydown', this.keydownEventHandler);
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutId);
    document.removeEventListener('keypress', this.keypressEventHandler);
    document.removeEventListener('keydown', this.keydownEventHandler);
  }

  state = {
    pin: ''
  };
  timeoutId: TimeoutID;

  keypressEventHandler = (event: KeyboardEvent) => {
    const { key, code, keyCode } = event;
    if ((includes(code, 'Digit') || includes(code, 'Numpad')) && !Number.isNaN(Number(key))) {
      this.addNextNumberToPin(Number(key));
    } else if (keyCode === ENTER_KEY_CODE) {
      this.logIn();
    }
  };

  keydownEventHandler = (event: KeyboardEvent) => {
    if (event.keyCode === BACKSPACE_KEY_CODE) {
      this.clearLastNumberOfPin();
    }
  };

  addNextNumberToPin(number: number) {
    if (this.state.pin.length < 4) {
      this.setState({ pin: `${this.state.pin}${number}` }, () => {
        if (this.state.pin.length === 4) {
          this.logIn();
        }
      });
    }
  }

  clearPin = () => {
    this.setState({ pin: '' });
  };

  clearLastNumberOfPin = () => {
    if (this.state.pin.length > 0) {
      this.setState({ pin: this.state.pin.slice(0, -1) });
    }
  };

  logIn = throttle(() => {
    if (this.state.pin.length === 4) {
      this.props.logIn(this.state.pin);
      this.timeoutId = setTimeout(this.clearPin, 300);
    }
  }, 300);

  onAnimationEnd = () => {
    this.props.onVisibilityChange(this.props.shouldShow);
  };

  render() {
    const coefficient =
      this.props.deviceHeight <= COLLIDE_HEIGHT && this.props.deviceWidth <= COLLIDE_WIDTH
        ? 100
        : 0;
    const scale =
      this.props.deviceHeight / (PIN_PAD_MINIMAL_HEIGHT + coefficient) > 1
        ? 1
        : this.props.deviceHeight / (PIN_PAD_MINIMAL_HEIGHT + coefficient);
    const margin = DEFAULT_PIN_PAD_MARGIN * scale;

    return (
      <Container
        className={this.props.shouldShow ? 'visible' : 'hidden'}
        isFirstRender={this.props.pinPadInitialMount}
        onAnimationEnd={this.onAnimationEnd}
        scale={scale}>
        <PinDisplay pin={this.state.pin} scale={scale} />
        <Row marginTop={margin}>
          <PinPadButton text="1" scale={scale} onPress={() => this.addNextNumberToPin(1)} />
          <PinPadButton text="2" scale={scale} onPress={() => this.addNextNumberToPin(2)} />
          <PinPadButton text="3" scale={scale} onPress={() => this.addNextNumberToPin(3)} />
        </Row>
        <Row marginTop={margin}>
          <PinPadButton text="4" scale={scale} onPress={() => this.addNextNumberToPin(4)} />
          <PinPadButton text="5" scale={scale} onPress={() => this.addNextNumberToPin(5)} />
          <PinPadButton text="6" scale={scale} onPress={() => this.addNextNumberToPin(6)} />
        </Row>
        <Row marginTop={margin}>
          <PinPadButton text="7" scale={scale} onPress={() => this.addNextNumberToPin(7)} />
          <PinPadButton text="8" scale={scale} onPress={() => this.addNextNumberToPin(8)} />
          <PinPadButton text="9" scale={scale} onPress={() => this.addNextNumberToPin(9)} />
        </Row>
        <Row marginTop={margin}>
          <PinPadButton text="C" scale={scale} onPress={this.clearPin} />
          <PinPadButton text="0" scale={scale} onPress={() => this.addNextNumberToPin(0)} />
          <PinPadButton icon={pinDelete} scale={scale} onPress={this.clearLastNumberOfPin} />
        </Row>
        <EnterButton marginTop={margin}>
          <RoundedCornerButton
            scale={scale}
            label="Enter"
            onPress={this.logIn}
            buttonTheme="brand"
          />
        </EnterButton>
      </Container>
    );
  }
}

PinPad.propTypes = {
  logIn: PropTypes.func,
  pinPadInitialMount: PropTypes.bool,
  shouldShow: PropTypes.bool,
  onVisibilityChange: PropTypes.func,
  deviceHeight: PropTypes.number,
  deviceWidth: PropTypes.number
};
